// Storefront coupon banner: the validity phrase, relative to the shopper's own day.
//
// `now` is passed in rather than read from the clock inside, so the formatting is pure and
// unit-testable — the same reasoning as deriveTrafficSource taking the referrer as an argument
// instead of reading `document`.
//
// ─── TIMEZONE ────────────────────────────────────────────────────────────────
// scpn_valid_till is `timestamp WITHOUT time zone`, and partner-ui always writes 23:59:59 on the
// end date. The value therefore names a CALENDAR DAY, not an instant, and is read as one: the
// date part is split off and handed to the Date(y, m, d) constructor.
//
// `new Date("2026-11-10T23:59:59")` would parse as UTC and shift backwards for anyone east of
// Greenwich — every Indian shopper — turning "ends 10 Nov" into "ends 9 Nov" and firing
// "today only" a full day early. No instant is constructed here, so no offset can be applied.
//
// "Today" comes from the caller's own clock. The SERVER cannot compute this phrase: the prod JVM
// runs UTC, so between 18:30 and 23:59 IST its "today" is already the shopper's tomorrow, and it
// has no way to know an international shopper's zone at all. The store-details payload is also
// fetched once per session and never refetched, so a server-baked string would freeze.

/**
 * Whole days from `now`'s local date to the y/m/d given.
 *
 * DST-safe: both sides are converted to exact UTC midnights before subtracting. Subtracting two
 * local Date objects across a clock change yields 23 or 25 hours and would round the wrong way.
 */
export function daysUntil(y, m, d, now = new Date()) {
  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const endUtc = Date.UTC(y, m - 1, d);
  return Math.round((endUtc - todayUtc) / 86400000);
}

/**
 * The COMPLETE phrase, never a fragment — "today only" must not end up rendered as
 * "till today only", so the preposition belongs here rather than in the JSX.
 *
 * Returns null when there is nothing to say: no value at all (a null valid_till means the coupon
 * never expires) or an unparseable one.
 */
export function formatValidity(value, now = new Date()) {
  if (!value) return null;

  const [datePart] = String(value).split("T");
  const [y, m, d] = String(datePart).split("-").map(Number);
  if (!y || !m || !d) return null;

  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;

  const days = daysUntil(y, m, d, now);
  if (days === 0) return "today only";
  if (days === 1) return "till tomorrow";

  // Negative should be unreachable — the backend only ever sends a coupon already inside its
  // window — but falling back to the date is far better than claiming "today only" about
  // something that has in fact ended.
  return `till ${dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`;
}
